import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { blockchainService } from '../../../services/blockchainService';

const AddressInputForm = ({ onStartInvestigation }) => {
  const [formData, setFormData] = useState({
    addresses: '',
    network: 'bitcoin',
    title: '',
    description: '',
    priority: 3
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  const supportedNetworks = blockchainService?.getSupportedNetworks();
  
  const validateAddress = (address, network) => {
    // Basic validation for different networks
    const validations = {
      bitcoin: /^[13][a-km-z A-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/,
      ethereum: /^0x[a-fA-F0-9]{40}$/,
      bitcoin_cash: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bitcoincash:[a-z0-9]{40,}$/,
      litecoin: /^[LM3][a-km-zA-HJ-NP-Z1-9]{26,33}$|^ltc1[a-z0-9]{39,59}$/
    };

    return validations?.[network]?.test(address?.trim());
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData?.addresses?.trim()) {
      setError('Please enter at least one cryptocurrency address');
      return;
    }

    if (!formData?.title?.trim()) {
      setError('Please enter a title for the investigation');
      return;
    }

    // Parse and validate addresses
    const addressList = formData?.addresses
      ?.split(/[\n,;]/)
      ?.map(addr => addr?.trim())
      ?.filter(addr => addr?.length > 0);

    if (addressList?.length === 0) {
      setError('Please enter valid cryptocurrency addresses');
      return;
    }

    // Validate address formats
    const invalidAddresses = addressList?.filter(addr => 
      !validateAddress(addr, formData?.network)
    );

    if (invalidAddresses?.length > 0) {
      setError(`Invalid ${formData?.network} address format: ${invalidAddresses?.[0]}`);
      return;
    }

    setIsAnalyzing(true);

    try {
      // Quick analysis of the first address to get initial data
      let addressData = null;
      if (addressList?.length > 0) {
        const analysisResult = await blockchainService?.analyzeAddress(
          addressList?.[0], 
          formData?.network
        );
        
        if (analysisResult?.error) {
          setError(`Address analysis failed: ${analysisResult?.error}`);
          setIsAnalyzing(false);
          return;
        }
        
        addressData = analysisResult;
      }

      // Create investigation data
      const investigationData = {
        title: formData?.title,
        description: formData?.description,
        priority: formData?.priority,
        addresses: addressList,
        network: formData?.network,
        addressData,
        timestamp: new Date()?.toISOString()
      };

      // Pass to parent handler
      onStartInvestigation?.(investigationData);

    } catch (error) {
      setError('Failed to start investigation. Please try again.');
      console.error('Investigation start error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleQuickScan = async () => {
    if (!formData?.addresses?.trim()) {
      setError('Please enter an address to scan');
      return;
    }

    const address = formData?.addresses?.split(/[\n,;]/)?.[0]?.trim();
    if (!validateAddress(address, formData?.network)) {
      setError('Please enter a valid address format');
      return;
    }

    setIsAnalyzing(true);

    try {
      const result = await blockchainService?.analyzeAddress(address, formData?.network);
      
      if (result?.error) {
        setError(`Quick scan failed: ${result?.error}`);
        return;
      }

      // Navigate to address risk assessment with the scan results
      window.location.href = `/address-risk-assessment?address=${address}&network=${formData?.network}`;
      
    } catch (error) {
      setError('Quick scan failed. Please try again.');
      console.error('Quick scan error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-forensic-card p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Search" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-text-primary">Start New Investigation</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Investigation Title */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Investigation Title <span className="text-error">*</span>
          </label>
          <Input
            type="text"
            placeholder="e.g., Suspicious Bitcoin Address Analysis"
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Description
          </label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={3}
            placeholder="Brief description of the investigation purpose..."
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
          />
        </div>

        {/* Network and Priority */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Blockchain Network
            </label>
            <select
              value={formData?.network}
              onChange={(e) => handleInputChange('network', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {supportedNetworks?.map(network => (
                <option key={network?.id} value={network?.id}>
                  {network?.name} ({network?.symbol})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Priority Level
            </label>
            <select
              value={formData?.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e?.target?.value))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value={1}>Low Priority</option>
              <option value={2}>Low-Medium Priority</option>
              <option value={3}>Medium Priority</option>
              <option value={4}>High Priority</option>
              <option value={5}>Critical Priority</option>
            </select>
          </div>
        </div>

        {/* Address Input */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Target Addresses <span className="text-error">*</span>
          </label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary font-mono text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={4}
            placeholder={`Enter ${formData?.network} addresses (one per line or comma-separated):\n\nExample:\n1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa\n3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy`}
            value={formData?.addresses}
            onChange={(e) => handleInputChange('addresses', e?.target?.value)}
            required
          />
          <p className="text-xs text-text-secondary mt-1">
            Multiple addresses can be separated by commas, semicolons, or new lines
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-error text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleQuickScan}
            disabled={isAnalyzing || !formData?.addresses?.trim()}
            loading={isAnalyzing}
            iconName="Zap"
            iconPosition="left"
          >
            Quick Scan
          </Button>

          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setFormData({
                  addresses: '',
                  network: 'bitcoin',
                  title: '',
                  description: '',
                  priority: 3
                });
                setError(null);
              }}
            >
              Clear
            </Button>

            <Button
              type="submit"
              disabled={isAnalyzing || !formData?.addresses?.trim() || !formData?.title?.trim()}
              loading={isAnalyzing}
              iconName="Search"
              iconPosition="left"
            >
              Start Investigation
            </Button>
          </div>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-start space-x-2 text-xs text-text-secondary">
          <Icon name="Info" size={14} color="currentColor" className="mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p><strong>Quick Scan:</strong> Instantly analyze a single address for risk factors</p>
            <p><strong>Start Investigation:</strong> Create a full forensic investigation with multiple addresses</p>
            <p><strong>Supported Networks:</strong> Bitcoin, Ethereum, Bitcoin Cash, and Litecoin</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressInputForm;